/**
 * Sperrt Partien und Dashboard, bis ein Konto mit Wegwerfadresse eine echte
 * Adresse bestätigt hat.
 *
 * Steht in `RequireAuth` an derselben Stelle wie die eigentliche Seite – nicht
 * als Overlay darüber. Ein Overlay müsste die Seite dahinter trotzdem laden
 * und vor Interaktion schützen; hier wird sie schlicht nicht gerendert.
 *
 * Betrifft nur Konten von vor der Wegwerf-Prüfung bei der Registrierung.
 * Neue Konten mit einer solchen Adresse werden gar nicht erst angelegt.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Card, Input, Notice } from '../ui';
import { api } from '../../lib/api';
import { useAuth } from '../../store/auth';

export function EmailReplacementGate() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestEmailChange(email);
      setSentTo(email);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card title="Neue E-Mail-Adresse bestätigen" className="w-full max-w-md">
        <div className="space-y-4 text-sm">
          {sentTo ? (
            <>
              <Notice tone="success">
                Wir haben einen Bestätigungslink an {sentTo} geschickt. Öffne ihn, um weiterzuspielen – diese
                Seite aktualisiert sich danach von selbst.
              </Notice>
              <p className="text-xs text-panel-500 dark:text-panel-400">
                Nichts angekommen? Sieh im Spam-Ordner nach, oder gib unten eine andere Adresse an.
              </p>
              <div className="flex items-center justify-between gap-2 border-t divider pt-3">
                <Button variant="ghost" size="sm" onClick={() => setSentTo(null)}>
                  Andere Adresse verwenden
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void logout()}>
                  Abmelden
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-panel-600 dark:text-panel-300">
                Die bei diesem Konto hinterlegte Adresse{user?.email ? <> (<strong>{user.email}</strong>)</> : ''}{' '}
                gehört zu einem Wegwerf-Postfach und lässt sich nicht erreichen. Gib eine echte Adresse an, um
                weiterzuspielen – deine Spielstände bleiben dabei erhalten.
              </p>
              <Input
                label="Neue E-Mail-Adresse"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="du@beispiel.de"
              />
              {error && <Notice tone="danger">{error}</Notice>}
              <div className="flex items-center justify-between gap-2">
                <Button type="submit" variant="primary" loading={busy}>
                  Bestätigungslink senden
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => void logout()}>
                  Abmelden
                </Button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
